import { useApiClient } from '@/composables/useApiClient'
import { usePauseCreateConfirmDialog } from '@/composables/usePauseCreateConfirmDialog'
import { useToast } from 'primevue'
import { useI18n } from 'vue-i18n'
import type { IPaginationResponse } from '../interfaces/pagination-response.interface'
import type { ICreatePause } from './interfaces/create-pause.interface'
import type { IFilterPause } from './interfaces/filter-pause.interface'
import type { IOptionsPause } from './interfaces/options-pause.interface'
import type { IUpdatePause } from './interfaces/update-pause.interface'
import type { PauseDto } from './interfaces/pause.interface'

type CachedPauses = {
  response: IPaginationResponse<PauseDto>
  expiresAt: number
}

const PAUSE_CACHE_PREFIX = 'calloperator:pauses:'
const PAUSE_CACHE_TTL_MS = 60 * 60 * 1000
const pauseRequests = new Map<string, Promise<IPaginationResponse<PauseDto>>>()

const getPauseStorageKey = (requestKey: string) => `${PAUSE_CACHE_PREFIX}${requestKey}`

const readPauseCache = (requestKey: string): IPaginationResponse<PauseDto> | null => {
  if (typeof sessionStorage === 'undefined') return null

  const storageKey = getPauseStorageKey(requestKey)
  try {
    const rawCache = sessionStorage.getItem(storageKey)
    if (!rawCache) return null

    const cache = JSON.parse(rawCache) as CachedPauses
    if (!cache.response || cache.expiresAt <= Date.now()) {
      sessionStorage.removeItem(storageKey)
      return null
    }

    return cache.response
  } catch {
    sessionStorage.removeItem(storageKey)
    return null
  }
}

const writePauseCache = (requestKey: string, response: IPaginationResponse<PauseDto>) => {
  if (typeof sessionStorage === 'undefined') return

  try {
    const cache: CachedPauses = {
      response,
      expiresAt: Date.now() + PAUSE_CACHE_TTL_MS,
    }
    sessionStorage.setItem(getPauseStorageKey(requestKey), JSON.stringify(cache))
  } catch {
    // La aplicaciÃ³n sigue funcionando si sessionStorage no estÃ¡ disponible.
  }
}

export const clearPauseCache = () => {
  pauseRequests.clear()
  if (typeof sessionStorage === 'undefined') return

  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index)
      if (key?.startsWith(PAUSE_CACHE_PREFIX)) sessionStorage.removeItem(key)
    }
  } catch {
    // No hay cachÃ© persistente que limpiar si sessionStorage no estÃ¡ disponible.
  }
}

export const usePauseService = () => {
  const privateApi = useApiClient(true)
  const toast = useToast()
  const { confirmPauseCreation } = usePauseCreateConfirmDialog()
  const { t } = useI18n()

  const showToast = (type: 'success' | 'error', messageKey: string) => {
    toast.add({
      severity: type,
      summary: t(type === 'success' ? 'general.success' : 'general.error'),
      detail: t(messageKey),
      life: 3000,
    })
  }

  const handleError = (error: unknown, messageKey: string) => {
    showToast('error', messageKey)
  }

  const isCreatePauseValid = (data: ICreatePause) =>
    Boolean(
      data.name?.trim() &&
        data.description?.trim() &&
        data.duration?.trim() &&
        data.status,
    )

  const getPauses = (query?: IFilterPause): Promise<IPaginationResponse<PauseDto>> => {
    const params = { ...query }
    const requestKey = JSON.stringify([
      params.page ?? null,
      params.size ?? null,
      params.filter ?? null,
      params.sort ?? null,
      params.direction ?? null,
    ])
    const cachedResponse = readPauseCache(requestKey)
    if (cachedResponse) return Promise.resolve(cachedResponse)

    const pendingRequest = pauseRequests.get(requestKey)

    if (pendingRequest) return pendingRequest

    const request = privateApi
      .get<IPaginationResponse<PauseDto>>('/pauses', {
        params: { ...query },
      })
      .then((response) => {
        writePauseCache(requestKey, response)
        return response
      })
      .catch((error: unknown) => {
        handleError(error, 'pause.error_fetching_all_pauses')
        throw error
      })
      .finally(() => {
        pauseRequests.delete(requestKey)
      })

    pauseRequests.set(requestKey, request)
    return request
  }

  const getAllPauses = async (): Promise<IOptionsPause[]> => {
    try {
      return await privateApi.get<IOptionsPause[]>('/pauses/options')
    } catch (error) {
      handleError(error, 'pause.error_fetching_all_pauses')
      throw error
    }
  }

  const getPauseById = async (pause_id: string): Promise<IUpdatePause> => {
    try {
      return await privateApi.get<IUpdatePause>(`/pauses/${pause_id}`)
    } catch (error) {
      handleError(error, 'pause.error_fetching_pause')
      throw error
    }
  }

  const createPause = async (data: ICreatePause): Promise<PauseDto> => {
    if (!isCreatePauseValid(data)) {
      showToast('error', 'pause.validation_required')
      throw new Error(t('pause.validation_required'))
    }

    await confirmPauseCreation()

    try {
      const response = await privateApi.post<PauseDto>('/pauses', data)
      clearPauseCache()
      showToast('success', 'pause.success_created')
      return response
    } catch (error) {
      handleError(error, 'pause.error_creating_pause')
      throw error
    }
  }

  const updatePause = async (pause_id: string, data: IUpdatePause): Promise<PauseDto> => {
    try {
      const response = await privateApi.patch<PauseDto>(`/pauses/${pause_id}`, data)
      clearPauseCache()
      showToast('success', 'pause.success_updated')
      return response
    } catch (error) {
      handleError(error, 'pause.error_updating_pause')
      throw error
    }
  }

  const deletePause = async (pause_id: string): Promise<PauseDto> => {
    try {
      const response = await privateApi.delete<PauseDto>(`/pauses/${pause_id}`)
      clearPauseCache()
      showToast('success', 'pause.success_deleted')
      return response
    } catch (error) {
      handleError(error, 'pause.error_deleting_pause')
      throw error
    }
  }

  return {
    getPauses,
    getAllPauses,
    getPauseById,
    createPause,
    updatePause,
    deletePause,
  }
}
